import axios from "axios";
import { encryptPayload, decryptPayload } from "../utils/aes";
import store from "../redux/store";
import { actions } from "../redux/slices/userSlice";
import { accessTokenApi } from "./LiveApi";

const API_BASE_URL = import.meta.env.VITE_BACKEND_API_URL;
const secureAxios = axios.create({ baseURL: API_BASE_URL });

let isRefreshing = false;
let failedQueue = [];
const secureRoutes = [
  "/v1/admin/auth",
  // add more here
];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

/* REQUEST → ENCRYPT */
secureAxios.interceptors.request.use(async (config) => {
  // const token = store.getState().userReducer.token;
  // if (token) config.headers.Authorization = `Bearer ${token}`;
  const role = store.getState().userReducer.role;
  const method = config.method?.toLowerCase();

  const isSecureRoute = secureRoutes.some(route =>
    config.url.includes(route)
  );

  // POST / PUT / PATCH → Encrypt Body
  if (
    config.data &&
    ["post", "put", "patch"].includes(method) &&
    (role === "admin" || role === "mops" || isSecureRoute)
  ) {
    const encrypted = await encryptPayload(config.data);

    config.data = {
      encrypted: true,
      payload: encrypted,
    };

    config.headers["x-encrypted"] = "true";
  } else if (method !== "get") {
    config.headers["x-skip"] = "true";
  }

  // GET → Encrypt Query Params
  if (
    method === "get" &&
    (role === "admin" || role === "mops" || role === "subAdmin")
  ) {
    const encrypted = await encryptPayload(config.params);

    config.params = {
      data: encrypted.data,
      iv: encrypted.iv,
    };
    config.headers["x-encrypted"] = "true";
  } else if (!["post", "put", "patch"].includes(method)) {
    config.headers["x-skip"] = "true";
  }

  return config;
});

/* RESPONSE → DECRYPT */
secureAxios.interceptors.response.use(
  async (response) => {
    if (response.data?.encrypted) {
      const { data, iv } = response.data;
      response.data = await decryptPayload({ data, iv });
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // 1. If we get a 401 and we haven't tried to refresh yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return secureAxios(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true; // Mark as retried to prevent loops
      isRefreshing = true;

      try {
        const refreshToken = store.getState().userReducer.refreshToken;
        if(!refreshToken){
          return
        }
        const res = await accessTokenApi(refreshToken);

        const newAccessToken = res?.accessToken;
        store.dispatch(actions.setItem(["token", newAccessToken]));

        // return;
        processQueue(null, newAccessToken);
        // Update the header and retry the original request
        originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
        return secureAxios(originalRequest);
      } catch (err) {
        processQueue(err, null);
        // 2. If the refresh token call fails (401 or 403),
        // it means the refresh token is dead. LOG OUT.
        // store.dispatch(actions.logout());

        // window.location.href = "/login";
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

export default secureAxios;